// Path: ./src/app/components/AccountSidebarDrawer/index.tsx
import * as React from "react";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import { ControlledAccordions } from "../AccordionMenu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import { IconButton } from "@mui/material";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { LinkComponent } from "../LinkComponent";
import {
  DrawerWrapper,
  DrawerContainer,
  LinkWrapper,
  StandAloneLinkWrapper,
} from "./styled";
import { PermissionComponentWrapper } from "../PermissionComponentWrapper";

const DRAWER_WIDTH = 260;

interface LinkItem {
  linkPath: string;
  linkText: string;
  dataTestId?: string;
  roleName: string[];
  groupName: string[];
}

interface LinkListItem {
  title: string;
  dataTestId?: string;
  linkPath?: string;
  roleName: string[];
  groupName: string[];
  listItems?: LinkItem[];
}

interface Props {
  window?: () => Window;
  linkList: LinkListItem[];
}

export function AccountSideBarDrawer(props: Props) {
  const { window, linkList } = props;
  const [mobileOpen, setMobileOpen] = React.useState(false);

  /**
   * Toggles the mobile drawer's open/closed state.
   * Updates the state of the mobile drawer to its opposite value.
   */
  const handleDrawerToggle = React.useCallback(() => {
    setMobileOpen((prev) => !prev);
  }, []);

  const renderStandAloneLink = React.useCallback(
    (item: LinkListItem) => (
      <PermissionComponentWrapper
        requiredPermission={item.roleName}
        requiredPermissionGroup={item.groupName}
        wrapperCss="display: block;"
      >
        <StandAloneLinkWrapper>
          <LinkComponent
            pathTo={item.linkPath!}
            title={item.title}
            dataTestId={item.dataTestId}
          />
        </StandAloneLinkWrapper>
      </PermissionComponentWrapper>
    ),
    [],
  );

  const renderAccordionLinks = React.useCallback(
    (item: LinkListItem) => (
      <PermissionComponentWrapper
        requiredPermission={item.roleName}
        requiredPermissionGroup={item.groupName}
        wrapperCss="display: block;"
      >
        <ControlledAccordions title={item.title} dataTestId={item.dataTestId}>
          <LinkWrapper>
            {item.listItems?.map((subItem) => (
              <PermissionComponentWrapper
                key={subItem.linkText}
                requiredPermission={subItem.roleName}
                requiredPermissionGroup={subItem.groupName}
                wrapperCss="display: block;"
              >
                <LinkComponent
                  pathTo={subItem.linkPath}
                  title={subItem.linkText}
                  dataTestId={subItem.dataTestId}
                />
              </PermissionComponentWrapper>
            ))}
          </LinkWrapper>
        </ControlledAccordions>
      </PermissionComponentWrapper>
    ),
    [],
  );

  const drawer = React.useMemo(
    () => (
      <DrawerWrapper>
        <Box
          sx={{
            display: { xs: "flex", sm: "none" },
            color: "#ee8129",
            justifyContent: "flex-end",
          }}
        >
          <IconButton
            color="inherit"
            aria-label="Close drawer"
            onClick={handleDrawerToggle}
          >
            <KeyboardBackspaceIcon sx={{ fontSize: 30 }} />
          </IconButton>
        </Box>
        <Divider />
        <DrawerContainer role="navigation" aria-label="Account sidebar menu">
          {linkList.map((item, index) => (
            <React.Fragment key={`${item.title}-${index}`}>
              {item.linkPath
                ? renderStandAloneLink(item)
                : renderAccordionLinks(item)}
            </React.Fragment>
          ))}
        </DrawerContainer>
      </DrawerWrapper>
    ),
    [linkList, handleDrawerToggle, renderStandAloneLink, renderAccordionLinks],
  );

  const container =
    window !== undefined ? () => window().document.body : undefined;

  return (
    <>
      <Box
        component="nav"
        sx={{ width: { sm: DRAWER_WIDTH }, flexShrink: { sm: 0 } }}
        aria-label="Account navigation"
      >
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: DRAWER_WIDTH,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            backgroundColor: "#4c4c4c",
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: DRAWER_WIDTH,
            },
            "& .MuiPaper-root.MuiDrawer-paper": {
              top: 64,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        sx={{
          display: { xs: "block", sm: "none" },
          position: "fixed",
          top: 5,
          left: 2,
          color: "#ee8129",
          zIndex: 9999,
        }}
      >
        <IconButton
          color="inherit"
          aria-label="Open navigation drawer"
          onClick={handleDrawerToggle}
        >
          <MenuOpenIcon sx={{ fontSize: 30 }} />
        </IconButton>
      </Box>
    </>
  );
}
